// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Download, Printer } from "lucide-react";
import EmailGate from "@/components/live-sheets/EmailGate";

const CYAN = "#06B6D4";

const COLS = [
  { key: "team_name", label: "Team Name", width: "w-48" },
  { key: "age_group", label: "Age Group", width: "w-28", editable: true, type: "text" },
  { key: "seed", label: "Seed", width: "w-20", editable: true, type: "number" },
  { key: "notes", label: "Notes", width: "w-64", editable: true, type: "text" },
  { key: "is_active", label: "Active", width: "w-20", editable: true, type: "bool" },
];

function EditableCell({ value, rowId, field, type, permission, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  const persist = async (val) => {
    onUpdate(rowId, field, val);
    const { error } = await supabase.from("circuit_team_registry").update({ [field]: val }).eq("id", rowId);
    if (error) onUpdate(rowId, field, value);
  };

  if (type === "bool") {
    if (permission !== "edit") return <span>{value ? "✓" : "—"}</span>;
    return (
      <input
        type="checkbox"
        className="h-4 w-4 cursor-pointer"
        checked={!!value}
        onChange={(e) => persist(e.target.checked)}
      />
    );
  }

  if (permission !== "edit") {
    return <span className="truncate block">{value ?? "—"}</span>;
  }

  const save = async () => {
    setEditing(false);
    const val = type === "number" ? (draft === "" || draft == null ? null : parseInt(draft, 10)) : draft;
    if (String(val ?? "") === String(value ?? "")) return;
    persist(val);
  };

  if (editing) {
    return (
      <input
        autoFocus
        type={type === "number" ? "number" : "text"}
        className="w-full px-1 border rounded text-sm"
        value={draft ?? ""}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === "Enter") save();
          if (e.key === "Escape") setEditing(false);
        }}
      />
    );
  }

  return (
    <span
      className="cursor-pointer hover:bg-cyan-50 px-1 block truncate"
      onClick={() => {
        setDraft(value);
        setEditing(true);
      }}
    >
      {value ?? "—"}
    </span>
  );
}

export default function CircuitSheetPage({ tokenOverride, embedded = false }: { tokenOverride?: string; embedded?: boolean } = {}) {
  const params = useParams<{ token: string }>();
  const token = tokenOverride ?? params.token;
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [gatePassed, setGatePassed] = useState(false);

  const [search, setSearch] = useState("");
  const [ageFilter, setAgeFilter] = useState("All");
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const fetchData = useCallback(async () => {
    if (!token) return;
    const { data: result, error: rpcErr } = await supabase.rpc("get_circuit_sheet_data", { p_token: token });
    if (rpcErr) {
      setError("This link is invalid or has expired.");
      setLoading(false);
      return;
    }
    const parsed = typeof result === "string" ? JSON.parse(result) : result;
    if (parsed?.error) {
      setError("This link is invalid or has expired.");
      setLoading(false);
      return;
    }
    setError(null);
    setData(parsed);
    setLoading(false);
  }, [token]);

  useEffect(() => {
    setLoading(true);
    fetchData();
  }, [fetchData]);

  const permission = data?.permission || "view";

  const needsGate = useMemo(() => {
    if (!data) return false;
    const allowed = data.allowed_emails;
    if (!Array.isArray(allowed) || allowed.length === 0) return false;
    if (gatePassed) return false;
    const stored = (localStorage.getItem("sheet_viewer_email") || "").toLowerCase();
    if (stored && allowed.map((e) => e.toLowerCase()).includes(stored)) return false;
    return true;
  }, [data, gatePassed]);

  const teams = data?.teams || [];

  const onUpdate = useCallback((rowId, field, value) => {
    setData((prev) => {
      if (!prev) return prev;
      return { ...prev, teams: prev.teams.map((t) => (t.id === rowId ? { ...t, [field]: value } : t)) };
    });
  }, []);

  const ageOptions = useMemo(() => {
    const set = new Set<string>();
    teams.forEach((t) => t.age_group && set.add(t.age_group));
    return ["All", ...Array.from(set).sort()];
  }, [teams]);

  const filtered = useMemo(() => {
    let rows = [...teams];
    const q = search.toLowerCase().trim();
    if (q) rows = rows.filter((r) => String(r.team_name ?? "").toLowerCase().includes(q));
    if (ageFilter !== "All") rows = rows.filter((r) => r.age_group === ageFilter);
    if (sortKey) {
      rows.sort((a, b) => {
        const av = sortKey === "seed" ? Number(a.seed) || 0 : String(a[sortKey] ?? "").toLowerCase();
        const bv = sortKey === "seed" ? Number(b.seed) || 0 : String(b[sortKey] ?? "").toLowerCase();
        if (av < bv) return sortDir === "asc" ? -1 : 1;
        if (av > bv) return sortDir === "asc" ? 1 : -1;
        return 0;
      });
    }
    return rows;
  }, [teams, search, ageFilter, sortKey, sortDir]);

  const summary = useMemo(() => {
    const byAge = {};
    teams.forEach((t) => {
      const a = t.age_group || "—";
      byAge[a] = (byAge[a] || 0) + 1;
    });
    return { teams: teams.length, byAge };
  }, [teams]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const exportCsv = () => {
    const headers = COLS.map((c) => c.label);
    const lines = [headers.join(",")];
    filtered.forEach((r) => {
      const vals = [r.team_name, r.age_group, r.seed, r.notes, r.is_active ? "Yes" : "No"];
      lines.push(vals.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `circuit_${(data?.circuit?.name || "sheet").replace(/\s+/g, "_")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center text-slate-500">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading sheet…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="rounded-lg border bg-white p-8 text-center shadow-sm">
          <p className="text-lg font-semibold text-slate-800">Sheet unavailable</p>
          <p className="mt-1 text-sm text-slate-500">{error || "This link is invalid or has expired."}</p>
        </div>
      </div>
    );
  }

  if (needsGate) {
    return <EmailGate allowedEmails={data.allowed_emails || []} eventName={data.circuit?.name} onPassed={() => setGatePassed(true)} />;
  }

  const circuit = data.circuit || {};

  return (
    <div className={embedded ? "flex flex-col bg-white" : "h-screen flex flex-col overflow-hidden bg-white"}>
      {!embedded && (
        <div className="px-6 py-4 text-white flex items-center justify-between" style={{ backgroundColor: CYAN }}>
          <div>
            <h1 className="text-xl font-bold leading-tight">{circuit.name}</h1>
          </div>
          <div className="flex items-center gap-3">
            <button className="inline-flex items-center gap-1 rounded bg-white/20 px-3 py-1.5 text-sm font-medium hover:bg-white/30" onClick={exportCsv}>
              <Download className="h-4 w-4" /> CSV
            </button>
            <button className="inline-flex items-center gap-1 rounded bg-white/20 px-3 py-1.5 text-sm font-medium hover:bg-white/30" onClick={() => window.print()}>
              <Printer className="h-4 w-4" /> Print
            </button>
          </div>
        </div>
      )}

      <div className="px-6 py-2 text-sm text-white flex flex-wrap gap-x-6 gap-y-1" style={{ backgroundColor: "rgba(6,182,212,0.9)" }}>
        <span>Teams: {summary.teams}</span>
        {Object.entries(summary.byAge).map(([age, n]) => (
          <span key={age} className="rounded bg-white/20 px-2">{age}: {n}</span>
        ))}
      </div>

      <div className="bg-slate-50 px-6 py-2 flex gap-3 items-center border-b">
        <input
          className="h-9 w-64 rounded-md border border-input bg-background px-3 text-sm"
          placeholder="Search team"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select className="h-9 rounded-md border border-input bg-background px-2 text-sm" value={ageFilter} onChange={(e) => setAgeFilter(e.target.value)}>
          {ageOptions.map((a) => (
            <option key={a} value={a}>{a === "All" ? "All Age Groups" : a}</option>
          ))}
        </select>
        <span className="ml-auto text-sm text-slate-500">{filtered.length} of {teams.length} teams</span>
      </div>

      <div className="flex-1 overflow-auto">
        <table className="border-collapse min-w-max text-sm">
          <thead className="sticky top-0 z-10 bg-slate-100">
            <tr>
              {COLS.map((c) => (
                <th
                  key={c.key}
                  onClick={() => toggleSort(c.key)}
                  className={`${c.width} border px-2 py-2 text-left font-semibold text-slate-700 cursor-pointer select-none hover:bg-slate-200`}
                >
                  {c.label}
                  {sortKey === c.key ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((r, i) => (
              <tr key={r.id} className={i % 2 ? "bg-slate-50/60" : "bg-white"}>
                {COLS.map((c) => {
                  const cls = `${c.width} border px-2 py-1 align-top`;
                  if (c.editable) {
                    return (
                      <td key={c.key} className={cls}>
                        <EditableCell value={r[c.key]} rowId={r.id} field={c.key} type={c.type} permission={permission} onUpdate={onUpdate} />
                      </td>
                    );
                  }
                  return <td key={c.key} className={cls}><span className="truncate block">{r[c.key] ?? "—"}</span></td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
